import React, { useState } from 'react';
import { TeamPosition, TeamCandidate, CandidateSource } from '../../types/team';
import { CandidateChip } from './CandidateChip';

export interface PositionCardProps {
  position: TeamPosition;
  onConfirm?: (positionId: number) => void;
  onDelete?: (positionId: number) => void;
  onEdit?: (positionId: number) => void;
  onAddCandidate?: (positionId: number, personId: number, source: CandidateSource) => void;
  onSelectCandidate?: (positionId: number, personId: number) => void;
  onRemoveCandidate?: (positionId: number, personId: number) => void;
  onReorderCandidates?: (positionId: number, fromIndex: number, toIndex: number) => void;
  onViewCandidate?: (personId: number) => void;
  // Filter by position props
  isFilterActive?: boolean;
  onFilterByPosition?: (positionId: number) => void;
  // Highlight when chat is searching for candidates
  isHighlighted?: boolean;
}

export const PositionCard: React.FC<PositionCardProps> = ({
  position,
  onConfirm,
  onDelete,
  onEdit,
  onAddCandidate,
  onSelectCandidate,
  onRemoveCandidate,
  onReorderCandidates,
  onViewCandidate,
  isFilterActive,
  onFilterByPosition,
  isHighlighted,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragOverSlotIndex, setDragOverSlotIndex] = useState<number | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragTargetIndex, setDragTargetIndex] = useState<number | null>(null);

  const isProposed = position.status === 'proposed';
  const candidates = position.candidates || [];
  const selectedCandidates = candidates.filter(c => c.status === 'selected');
  const proposedCandidates = candidates.filter(c => c.status === 'proposed');
  const allCandidates = [...selectedCandidates, ...proposedCandidates];
  const totalSlots = position.count_needed ?? 1;
  const filledSlots = selectedCandidates.length;
  // Empty slot UI rules:
  // - show no empty slots when requested positions are already filled
  // - show only one empty slot when there are no candidates yet
  // - otherwise show remaining requested slots
  const unfilledSlots = filledSlots >= totalSlots
    ? 0
    : allCandidates.length === 0
      ? 1
      : Math.max(0, totalSlots - filledSlots);

  // Priority colors
  const priorityColors = {
    critical: 'border-red-500 bg-red-900/20',
    important: 'border-yellow-500 bg-yellow-900/20',
    'nice-to-have': 'border-gray-500 bg-gray-900/20',
  };

  const handleDragOver = (e: React.DragEvent) => {
    const types = Array.from(e.dataTransfer.types);
    if (types.includes('application/x-candidate-reorder')) {
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    setDragOverSlotIndex(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    setIsDragOver(false);
    setDragOverSlotIndex(null);

    const plainData = e.dataTransfer.getData('text/plain');
    if (plainData && plainData.startsWith('candidate:')) {
      if (draggedIndex !== null && dragTargetIndex !== null && draggedIndex !== dragTargetIndex && onReorderCandidates) {
        e.preventDefault();
        onReorderCandidates(position.id, draggedIndex, dragTargetIndex);
      }
      setDraggedIndex(null);
      setDragTargetIndex(null);
      return;
    }

    e.preventDefault();

    try {
      let rawData = e.dataTransfer.getData('person');
      if (!rawData) {
        rawData = e.dataTransfer.getData('application/json');
      }
      if (!rawData) {
        if (plainData && plainData.startsWith('{')) {
          rawData = plainData;
        }
      }
      
      if (!rawData) {
        console.warn('No drag data found');
        return;
      }
      
      const data = JSON.parse(rawData);
      const personId = data.id || data.person_id;
      
      if (personId && onAddCandidate) {
        onAddCandidate(position.id, personId, 'drag_drop');
      }
    } catch (error) {
      console.error('Failed to parse dropped data:', error);
    }
  };

  // Drop zone specific handlers
  const handleDropZoneDragOver = (e: React.DragEvent, slotIndex: number) => {
    const types = Array.from(e.dataTransfer.types);
    if (types.includes('application/x-candidate-reorder')) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverSlotIndex(slotIndex);
  };

  const handleDropZoneDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverSlotIndex(null);
  };

  const handleDropZoneDrop = (e: React.DragEvent) => {
    setDragOverSlotIndex(null);
    handleDrop(e);
  };

  // Handlers for candidate reordering
  const handleCandidateDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleCandidateDragOver = (index: number) => {
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragTargetIndex(index);
    }
  };

  const handleCandidateDrop = (toIndex: number) => {
    if (draggedIndex !== null && draggedIndex !== toIndex && onReorderCandidates) {
      onReorderCandidates(position.id, draggedIndex, toIndex);
    }
    setDraggedIndex(null);
    setDragTargetIndex(null);
  };

  const handleCandidateDragEnd = () => {
    setDraggedIndex(null);
    setDragTargetIndex(null);
  };

  // Handle click on card header to filter people list
  const handleFilterClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    if (onFilterByPosition && position.skills_required && position.skills_required.length > 0) {
      onFilterByPosition(position.id);
    }
  };

  return (
    <div
      data-testid="position-card"
      className={`
        rounded-lg border-2 p-3 transition-all min-w-[220px] max-w-[300px]
        ${priorityColors[position.priority]}
        ${isProposed ? 'opacity-80 border-dashed' : ''}
        ${isDragOver ? 'ring-2 ring-brand-blue scale-105' : ''}
        ${isFilterActive ? 'ring-2 ring-cyan-400 shadow-lg shadow-cyan-500/20' : ''}
        ${isHighlighted ? 'ring-2 ring-purple-500 animate-pulse shadow-lg shadow-purple-500/30' : ''}
      `}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header - clickable for filtering */}
      <div 
        className={`flex items-start justify-between mb-2 cursor-pointer rounded-lg -m-1 p-1 transition-colors ${
          onFilterByPosition && position.skills_required?.length ? 'hover:bg-gray-700/50' : ''
        }`}
        onClick={handleFilterClick}
        title={position.skills_required?.length ? (isFilterActive ? 'Click to clear filter' : 'Click to filter people list by this position\'s skills') : 'No skills defined for filtering'}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {isFilterActive && (
              <span className="text-cyan-400" title="Filtering people list">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
              </span>
            )}
            <h4 className={`font-semibold truncate text-sm ${isFilterActive ? 'text-cyan-300' : 'text-dark-text'}`}>
              {position.role}
            </h4>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-xs px-1.5 py-0.5 rounded ${
              position.priority === 'critical' ? 'bg-red-500/30 text-red-300' :
              position.priority === 'important' ? 'bg-yellow-500/30 text-yellow-300' :
              'bg-gray-500/30 text-gray-300'
            }`}>
              {position.priority}
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded ${
              allCandidates.length > totalSlots 
                ? 'bg-amber-500/30 text-amber-300' 
                : filledSlots === totalSlots 
                  ? 'bg-green-500/30 text-green-300'
                  : 'text-gray-400'
            }`}>
              {filledSlots}/{totalSlots} filled
            </span>
            {isFilterActive && (
              <span className="text-xs text-cyan-400 font-medium">
                🔍
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 ml-2">
          {onEdit && (
            <button
              data-testid="edit-position"
              onClick={() => onEdit(position.id)}
              className="p-1 hover:bg-blue-600/30 rounded text-blue-400 transition-colors"
              title="Edit position details"
              aria-label="Edit position"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}
          {isProposed && onConfirm && (
            <button
              onClick={() => onConfirm(position.id)}
              className="p-1 hover:bg-green-600/30 rounded text-green-400 transition-colors"
              title="Confirm position"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
          )}
          {onDelete && (
            <button
              data-testid="delete-position"
              onClick={() => onDelete(position.id)}
              className="p-1 hover:bg-red-600/30 rounded text-red-400 transition-colors"
              title="Delete position"
              aria-label="Delete position"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Candidates */}
      <div className="space-y-1">
        {/* Scrollable candidate list - keeps enough vertical space for ~3 cards */}
        {allCandidates.length > 0 && (
          <div className="relative">
            <div
              className={`space-y-1 ${allCandidates.length > 3 ? 'max-h-[192px] overflow-y-auto pr-0.5 scrollbar-thin' : ''}`}
              style={allCandidates.length > 3 ? { scrollbarWidth: 'thin', scrollbarColor: '#4b5563 transparent' } : undefined}
            >
              {allCandidates.map((candidate: TeamCandidate, index: number) => {
                const isSpare = index >= totalSlots;

                return (
                  <CandidateChip
                    key={candidate.person_id}
                    personId={candidate.person_id}
                    index={index}
                    totalCandidates={allCandidates.length}
                    status={candidate.status as 'selected' | 'proposed' | 'rejected'}
                    source={candidate.source as 'ai_suggested' | 'user_selected' | 'drag_drop'}
                    matchScore={candidate.match_score}
                    scores={candidate.scores}
                    matchReasoning={candidate.match_reasoning}
                    profile={candidate.profile}
                    isSpare={isSpare}
                    onSelect={candidate.status === 'proposed' ? () => onSelectCandidate?.(position.id, candidate.person_id) : undefined}
                    onRemove={() => onRemoveCandidate?.(position.id, candidate.person_id)}
                    onClick={() => onViewCandidate?.(candidate.person_id)}
                    onDragStart={handleCandidateDragStart}
                    onDragOver={handleCandidateDragOver}
                    onDrop={handleCandidateDrop}
                    onDragEnd={handleCandidateDragEnd}
                    isDragTarget={dragTargetIndex === index}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Empty drop zones - one for each unfilled slot */}
        {Array.from({ length: unfilledSlots }).map((_, slotIndex) => (
          <div
            key={`slot-${slotIndex}`}
            data-testid="position-drop-zone"
            className={`
              border-2 border-dashed rounded-lg p-2 text-center transition-all
              ${dragOverSlotIndex === slotIndex 
                ? 'border-blue-500 bg-blue-900/30 text-blue-300' 
                : 'border-gray-600 text-gray-500 hover:border-gray-500 hover:bg-gray-800/50'
              }
            `}
            onDragOver={(e) => handleDropZoneDragOver(e, slotIndex)}
            onDragLeave={handleDropZoneDragLeave}
            onDrop={handleDropZoneDrop}
          >
            <div className="flex items-center justify-center gap-2">
              <div className={`
                w-5 h-5 rounded-full border-2 flex items-center justify-center
                ${dragOverSlotIndex === slotIndex ? 'border-blue-500' : 'border-gray-600'}
              `}>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <span className="text-xs">
                {dragOverSlotIndex === slotIndex ? 'Release to add' : 'Drop candidate here'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* AI reasoning */}
      {position.reasoning && (
        <div className="mt-2 pt-2 border-t border-gray-700">
          <p className="text-xs text-gray-400 italic line-clamp-2">
            {position.reasoning}
          </p>
        </div>
      )}
    </div>
  );
};

export default PositionCard;
